import React, { useState, useEffect } from 'react';
import { X, Heart, ShoppingBag, Check } from 'lucide-react';
import { API_BASE } from '../utils/getImageUrl';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import ImageZoom from './ImageZoom';
import ImageLightbox from './ImageLightbox';

const AVAILABLE_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const ProductModal = ({ product, isOpen, onClose }) => {
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const { t, language } = useLanguage();
  const { convertPrice, getCurrencySymbol } = useCurrency();

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const imagesList = product ? Array.from(product.images || []) : [];

  // Şəkil yolunu tam URL-ə çevirir
  const getImageUrl = (imgInput) => {
    let rawImg = imgInput;

    if (!rawImg && product) {
      if (Array.isArray(product.images) && product.images.length > 0) {
        rawImg = product.images[0];
      } else {
        rawImg = product.image || product.imageUrl;
      }
    }

    if (!rawImg || typeof rawImg !== 'string' || rawImg.trim() === '') {
      return 'https://via.placeholder.com/400x400?text=No+Image';
    }

    if (rawImg.startsWith('http://') || rawImg.startsWith('https://') || rawImg.startsWith('data:image')) {
      return rawImg;
    }

    const cleanPath = rawImg.startsWith('/') ? rawImg : `/${rawImg}`;
    const encodedPath = cleanPath
      .split('/')
      .map((segment) => encodeURIComponent(segment))
      .join('/');

    return `${API_BASE}${encodedPath}`;
  };

  // sizesStock-u təhlükəsiz şəkildə obyektə çeviririk
  const getParsedSizesStock = () => {
    if (!product || !product.sizesStock) return null;
    if (typeof product.sizesStock === 'object') return product.sizesStock;
    try {
      return JSON.parse(product.sizesStock);
    } catch (e) {
      console.error('sizesStock JSON parse xətası:', e);
      return null;
    }
  };

  const parsedSizesStock = getParsedSizesStock();

  const getStockForSize = (size) => {
    if (!parsedSizesStock) {
      return Number(product?.stockQuantity ?? product?.stock ?? 0);
    }
    const val = parsedSizesStock[size];
    if (val === undefined || val === null) return 0;
    return Number(val);
  };

  const totalStockCount = parsedSizesStock
    ? Object.keys(parsedSizesStock).reduce((acc, size) => acc + getStockForSize(size), 0)
    : Number(product?.stockQuantity ?? product?.stock ?? 0);

  const isEntirelyOutOfStock = totalStockCount <= 0;

  useEffect(() => {
    if (product) {
      const imgs = Array.from(product.images || []);
      const initialImage = imgs[0] || product.imageUrl || product.image;
      setSelectedImage(initialImage);
      setLightboxOpen(false);

      let parsed = product.sizesStock;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = null;
        }
      }

      const firstAvailable = AVAILABLE_SIZES.find((sz) => {
        if (!parsed) return Number(product?.stockQuantity ?? product?.stock ?? 0) > 0;
        const val = parsed[sz];
        return val !== undefined && val !== null && Number(val) > 0;
      });

      setSelectedSize(firstAvailable || '');
      setQuantity(1);
    }
  }, [product]);

  // Ölçü dəyişəndə say həmin ölçünün stokundan çox qalmasın.
  // Əvvəl: M-də 3 seçib S-ə (stok 1) keçəndə say 3 qalırdı və
  // backend sifarişi stok yoxlamasında rədd edirdi.
  useEffect(() => {
    if (!selectedSize) return;
    const stock = getStockForSize(selectedSize);
    setQuantity((q) => Math.max(1, Math.min(q, stock || 1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSize]);

  if (!isOpen || !product) return null;

  const validId = Number(product.id || product.productId || product._id);
  const productName =
    typeof product.name === 'object'
      ? product.name[language] || product.name.az
      : product.nameAz || product.name;

  const currentSizeStock = selectedSize ? getStockForSize(selectedSize) : 0;
  const isSelectedSizeOutOfStock = currentSizeStock <= 0;

  // Tam ekran üçün bütün şəkillər və cari şəklin indeksi
  const lightboxImages = imagesList.length > 0
    ? imagesList.map((img) => getImageUrl(img))
    : [getImageUrl(selectedImage)];
  const lightboxStart = Math.max(0, imagesList.indexOf(selectedImage));

  const handleAddToCart = () => {
    if (!validId || isNaN(validId)) {
      alert('Xəta: Məhsulun ID-si tapılmadı!');
      return;
    }
    if (!selectedSize) {
      alert('Zəhmət olmasa razmer seçin!');
      return;
    }
    if (isSelectedSizeOutOfStock) {
      alert('Seçilmiş razmer stokda bitib!');
      return;
    }

    const finalImageToSave = selectedImage || (imagesList.length > 0 ? imagesList[0] : '');

    addToCart({
      id: validId,
      productId: validId,
      name: productName,
      price: product.price,
      images: imagesList,
      image: finalImageToSave,
      size: selectedSize,
      color: selectedColor || 'Standard',
      quantity: Number(quantity) || 1,
    });

    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg max-w-2xl w-full p-4 sm:p-6 relative max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Bağla"
            className="absolute right-3 top-3 sm:right-4 sm:top-4 p-2 text-gray-400 hover:text-black transition-colors z-10 cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* ŞƏKİL QALEREYASI */}
            <div className="flex flex-col gap-3">
              <ImageZoom
                src={getImageUrl(selectedImage)}
                alt={productName}
                onOpen={() => setLightboxOpen(true)}
                className="aspect-square rounded-lg bg-gray-100 border border-gray-100"
              />

              {imagesList.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {imagesList.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedImage(img)}
                      aria-label={`Şəkil ${idx + 1}`}
                      className={`relative w-16 h-16 rounded border overflow-hidden shrink-0 transition-all cursor-pointer ${
                        selectedImage === img
                          ? 'border-black ring-1 ring-black'
                          : 'border-gray-200 hover:border-gray-400 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={getImageUrl(img)}
                        alt={`thumb-${idx}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/100x100?text=No+Image';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* MƏHSUL HAQQINDA MƏLUMAT */}
            <div className="flex flex-col justify-between">
              <div>
                <h2 className="text-2xl font-serif mb-2 pr-8">{productName}</h2>
                <p className="text-xl font-serif mb-4 font-semibold">
                  {convertPrice(product.price)} {getCurrencySymbol()}
                </p>
                <p className="text-gray-600 text-sm mb-6">
                  {typeof product.description === 'object'
                    ? product.description[language] || product.description.az
                    : product.descriptionAz || product.description}
                </p>

                {/* RAZMER SEÇİMİ */}
                {isEntirelyOutOfStock ? (
                  <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-600 text-xs font-semibold rounded-md">
                    Məhsulun bütün razmerləri bitmişdir.
                  </div>
                ) : (
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium">{t('size') || 'Ölçü'}</label>
                      {selectedSize && (
                        <span className="text-xs text-gray-400">
                          Stok: {currentSizeStock > 0 ? `${currentSizeStock} ədəd` : 'Bitib'}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {AVAILABLE_SIZES.map((size) => {
                        const sizeStock = getStockForSize(size);
                        const isSizeDisabled = sizeStock <= 0;

                        return (
                          <button
                            key={size}
                            type="button"
                            disabled={isSizeDisabled}
                            onClick={() => setSelectedSize(size)}
                            className={`min-w-[44px] h-11 px-2 rounded border text-sm font-medium transition-all relative ${
                              isSizeDisabled
                                ? 'bg-gray-100 border-gray-200 text-gray-300 line-through cursor-not-allowed opacity-40'
                                : selectedSize === size
                                ? 'border-black bg-black text-white'
                                : 'border-gray-200 hover:border-black text-gray-800 cursor-pointer'
                            }`}
                          >
                            {size}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* SAY SEÇİMİ */}
                {!isEntirelyOutOfStock && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-2">{t('quantity') || 'Say'}</label>
                    <div className="flex items-center gap-3 border w-fit rounded">
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        disabled={isSelectedSizeOutOfStock || quantity <= 1}
                        aria-label="Azalt"
                        className="w-10 h-10 hover:bg-gray-100 cursor-pointer disabled:opacity-30"
                      >
                        -
                      </button>
                      <span className="min-w-[24px] text-center font-medium">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.min(currentSizeStock || 1, quantity + 1))}
                        disabled={isSelectedSizeOutOfStock || quantity >= currentSizeStock}
                        aria-label="Artır"
                        className="w-10 h-10 hover:bg-gray-100 cursor-pointer disabled:opacity-30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* DÜYMƏLƏR */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleAddToCart}
                  disabled={isEntirelyOutOfStock || !selectedSize || isSelectedSizeOutOfStock}
                  className={`flex-1 py-3 rounded text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    added
                      ? 'bg-green-600 text-white cursor-default'
                      : isEntirelyOutOfStock || isSelectedSizeOutOfStock || !selectedSize
                      ? 'bg-gray-200 text-gray-400 border border-gray-200 cursor-not-allowed font-semibold'
                      : 'bg-black text-white hover:bg-gray-800 cursor-pointer'
                  }`}
                >
                  {added ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('added') || 'Əlavə edildi'}
                    </>
                  ) : isEntirelyOutOfStock || isSelectedSizeOutOfStock ? (
                    'STOKDA YOXDUR'
                  ) : (
                    <>
                      <ShoppingBag className="w-4 h-4" />
                      {t('addToCart') || 'Səbətə Əlavə Et'}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => toggleWishlist(product)}
                  aria-label="Seçilmişlərə əlavə et"
                  className={`p-3 border rounded hover:border-black transition-colors cursor-pointer ${
                    isInWishlist(validId) ? 'text-red-500 border-red-500' : 'text-gray-400'
                  }`}
                >
                  <Heart className="w-5 h-5 fill-current" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tam ekran — modalın XARİCİNDƏ, ki kliklər modalı bağlamasın */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        startIndex={lightboxStart}
        alt={productName}
      />
    </>
  );
};

export default ProductModal;
